import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { t, tOr } from '../i18n'
import { toolGroups, type ToolLink } from './nav'

/**
 * The tools menu on the bar: which tool you are in, and every other tool of
 * the job one click away, grouped.
 *
 * Closed, it is a single button, so it adds no second copy of any link to the
 * page: the left-hand list stays the only one a reader or a test meets until
 * somebody opens this. It closes on a choice, on Escape and on a click
 * anywhere else, and Escape hands focus back to the button that opened it.
 */
export function ToolsMenu({ links, current }: { links: ToolLink[]; current: string }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const toggle = useRef<HTMLButtonElement>(null)
  const { pathname } = useLocation()

  // A route change is a choice made, whichever link made it.
  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      toggle.current?.focus()
    }
    const onClick = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  return (
    <div className="tools" ref={box}>
      <button
        ref={toggle}
        type="button"
        className="tools-toggle"
        aria-expanded={open}
        aria-controls="tools-panel"
        onClick={() => setOpen((was) => !was)}
      >
        <span className="bar-caption">{t('tools.caption')}</span>
        <span>{current}</span>
      </button>
      {open ? (
        <nav id="tools-panel" className="tools-panel" aria-label={t('tools.menu')}>
          {toolGroups(links).map((group) => (
            <section key={group.key}>
              <h2>{tOr(`tools.group.${group.key}`, group.key)}</h2>
              <ul>
                {group.links.map((link) => (
                  <li key={link.code}>
                    <NavLink to={link.path} end={link.code === 'home'} data-locked={link.locked}>
                      {link.label}
                      {link.preview ? <span className="tag-preview">{t('nav.preview')}</span> : null}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
      ) : null}
    </div>
  )
}
